import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

interface VersionResponse {
    version: string; 
}

interface SoftwareVersionContextType {
    version: string | undefined;
    lastFetched: number | undefined;
    isFetching: boolean;
    refresh: (force?: boolean) => Promise<void>;
}

const CTX_DEFAULT: SoftwareVersionContextType = {
    version: undefined,
    lastFetched: undefined,
    isFetching: false,
    refresh: async () => { },
};

const SoftwareVersionContext = createContext<SoftwareVersionContextType>(CTX_DEFAULT);
export const useSoftwareVersion = () => useContext(SoftwareVersionContext);

const LS_KEY = "_mini_app_version_cache";
const TTL_MS = 5 * 60 * 1000; // 5 minutes

function readCache(): { v: string; ts: number } | undefined {
    if (typeof window === "undefined") return undefined;
    try {
        const raw = window.localStorage.getItem(LS_KEY);
        if (!raw) return undefined;
        const parsed = JSON.parse(raw) as { v: string; ts: number };
        if (typeof parsed?.v === "string" && typeof parsed?.ts === "number") return parsed;
    } catch { }
    return undefined;
}

function writeCache(v: string) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(LS_KEY, JSON.stringify({ v, ts: Date.now() }));
    } catch { }
}

function isFresh(ts?: number) {
    if (!ts) return false;
    return Date.now() - ts < TTL_MS;
}

export const SoftwareVersionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [version, setVersion] = useState<string | undefined>(undefined);
    const [lastFetched, setLastFetched] = useState<number | undefined>(undefined);
    const [isFetching, setIsFetching] = useState(false);
    const inflight = useRef<Promise<void> | null>(null);

    const fetchVersion = useCallback(async (force = false) => {
        if (inflight.current) return inflight.current;

        inflight.current = (async () => {
            try {
             
                const cached = readCache();
                if (!force && cached && isFresh(cached.ts)) {
                    setVersion(cached.v);
                    setLastFetched(cached.ts);
                    return; 
                }

                setIsFetching(true);
                const res = await fetch("/homeApi/GetVersion", { method: "GET" });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = (await res.json()) as VersionResponse;
                const v = data?.version ?? "N/A";
                setVersion(v);
                const ts = Date.now();
                setLastFetched(ts);
                writeCache(v);
            } catch (e: any) {
                console.log("error fetching version:", e);
            } finally {
                setIsFetching(false);
                inflight.current = null;
            }
        })();

        return inflight.current;
    }, []);

    useEffect(() => {
        const cached = readCache();
        if (cached) {
            setVersion(cached.v);
            setLastFetched(cached.ts);
        }
        if (!isFresh(cached?.ts)) {
            void fetchVersion();
        }
    }, [fetchVersion]);

    const value = useMemo<SoftwareVersionContextType>(() => ({
        version,
        lastFetched,
        isFetching,
        refresh: fetchVersion,
    }), [version, lastFetched, isFetching,fetchVersion]);

    return (
        <SoftwareVersionContext.Provider value={value}>
            {children}
        </SoftwareVersionContext.Provider>
    );
};
